import { CAPACITY_CLASS, capacityFor } from "../../../src/shared/capacity";
import { todayIso } from "../../../src/lib/dates";
import type { CapacityClass } from "../../../src/shared/capacity";
import type { Owner, RequestSummary } from "./types";

/**
 * The capacity model, computed on read from the request list and the roster.
 *
 * Everything here is derived and nothing is stored. The projection always
 * runs from today, so a request whose start was never recorded, or one that
 * was parked and came back, needs no special case there. The start date's
 * only job is to say how much of the estimate is already spent — and since
 * it is reset each time a request enters In progress, it marks the current
 * stretch of work, so days spent parked are never counted as days worked.
 * Work done before a pause is not credited either; that errs on the side of
 * showing someone as more loaded, never less.
 *
 * Kept DOM-free and pure (the `requestGroups` precedent) so the arithmetic
 * is unit-tested on its own, and `now` is a parameter so a test can prove
 * the date is read in the team's time zone rather than the browser's.
 *
 * Not modelled: holidays and leave. Roughly ten Brazilian national holidays
 * a year is a real effect, and projections will be optimistic across them.
 */

/** Beyond this, a projected date is a false commitment rather than a forecast. */
export const HORIZON_WEEKS = 12;

const DAY_MS = 86_400_000;

/** Which promise a projection breaks first, if any. */
export type Risk = { kind: "none" } | { kind: "commitment" | "needed-by" | "milestone"; date: string; daysLate: number };

export interface QueuedRequest {
  request: RequestSummary;
  estimateDays: number | null;
  /** Effort still ahead: the estimate less what has elapsed since the start
   *  for active work, the whole estimate for queued work; null while unsized. */
  remainingDays: number | null;
  /** Elapsed effort beyond the estimate on an active item — the honest number
   *  when someone started a thing and then got pulled elsewhere. 0 otherwise. */
  overBy: number;
  /** Running total down the person's queue, or null once an unsized item sits
   *  at or above this one — a forecast built on a hole is a guess. */
  cumulativeDays: number | null;
  /** The weekday the running total is reached (YYYY-MM-DD), or null past the
   *  horizon or below an unsized item. */
  projected: string | null;
  risk: Risk;
}

export interface PersonCapacity {
  owner: Owner;
  capacityDaysPerWeek: number;
  /** Capacity across the window. */
  availableDays: number;
  /** Active plus queued effort, with the count of items that could not be
   *  summed — paired so no renderer can show the days without the caveat. */
  committed: { days: number; unestimated: number };
  parked: { count: number; days: number };
  verifying: { count: number; days: number };
  /** In progress now. */
  active: QueuedRequest[];
  /** What is next, in order. */
  queue: QueuedRequest[];
}

export interface CapacityView {
  /** The calendar date the projection ran from, in the team's time zone. */
  today: string;
  weeks: number;
  /** Busiest first, ties by name. Everyone on the roster, plus anyone who
   *  owns a row but is not on it — see `buildCapacity`. */
  people: PersonCapacity[];
  /** Open work nobody owns. Not a person, but not droppable either. */
  unassigned: { count: number; days: number; unestimated: number };
}

/** Midnight UTC of an ISO date — the calendar arithmetic base. */
const utcDay = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);
const toIso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const isWeekday = (ms: number): boolean => {
  const d = new Date(ms).getUTCDay();
  return d !== 0 && d !== 6;
};

/** Mon–Fri days in [from, to). Zero when `to` is not after `from`. */
export function weekdaysBetween(from: string, to: string): number {
  let n = 0;
  for (let t = utcDay(from), end = utcDay(to); t < end; t += DAY_MS) if (isWeekday(t)) n += 1;
  return n;
}

/** The relative order of a person's work: what they are doing now comes
 *  first, then the queue in the list's own order. A P3 in progress really does
 *  finish before a P1 that is merely triaged — unless they drop it. */
function classOf(r: RequestSummary): CapacityClass {
  return CAPACITY_CLASS[r.status];
}

/**
 * Walk one person's work, accruing capacity per weekday from today.
 *
 * The cursor starts at today and each weekday adds `capacity / 5` effort-days;
 * an item's projected date is the first weekday on which the running total
 * reaches its cumulative effort. Accrual continues across items, which is
 * what makes the second item's date depend on the first's. Zero capacity can
 * never reach anything, so it yields null rather than looping forever.
 */
function project(items: QueuedRequest[], today: string, capacityDaysPerWeek: number): void {
  const perWeekday = capacityDaysPerWeek / 5;
  const horizon = utcDay(today) + HORIZON_WEEKS * 7 * DAY_MS;
  // Repeated addition of 0.8 can land a hair under a whole number; a hair is
  // not a missed day.
  const EPS = 1e-9;
  let cumulative = 0;
  let blocked = false;
  // The cursor sits on the first working day, today or the coming Monday,
  // with that day's capacity already in hand. Each day is accrued once.
  let cursor = utcDay(today);
  while (!isWeekday(cursor)) cursor += DAY_MS;
  let accrued = perWeekday;

  for (const item of items) {
    if (blocked || item.remainingDays === null) {
      blocked = true;
      item.cumulativeDays = null;
      item.projected = null;
      continue;
    }
    cumulative += item.remainingDays;
    item.cumulativeDays = cumulative;
    if (perWeekday <= 0) {
      item.projected = null;
      continue;
    }
    while (accrued + EPS < cumulative && cursor <= horizon) {
      cursor += DAY_MS;
      if (isWeekday(cursor)) accrued += perWeekday;
    }
    if (cursor > horizon) {
      item.projected = null;
      blocked = true;
    } else {
      item.projected = toIso(cursor);
    }
  }
}

/** The earliest promise the projection runs past, named, or none. */
function riskOf(r: RequestSummary, projected: string | null): Risk {
  if (!projected) return { kind: "none" };
  const promises: { kind: "commitment" | "needed-by" | "milestone"; date: string | null }[] = [
    { kind: "commitment", date: r.nextDate },
    { kind: "needed-by", date: r.neededBy },
    { kind: "milestone", date: r.milestone?.eventDate ?? null },
  ];
  const late = promises
    .filter((p): p is { kind: "commitment" | "needed-by" | "milestone"; date: string } => p.date !== null && projected > p.date)
    .sort((a, b) => a.date.localeCompare(b.date))[0];
  if (!late) return { kind: "none" };
  return { kind: late.kind, date: late.date, daysLate: Math.round((utcDay(projected) - utcDay(late.date)) / DAY_MS) };
}

/**
 * How much of each active item's estimate is already spent.
 *
 * Each past weekday's capacity is shared among the items that had started by
 * then — three things in progress for a week did not each get a full week.
 * Today's work belongs to the projection, so the range is [start, today).
 */
function elapsedByItem(active: RequestSummary[], today: string, perWeekday: number): Map<string, number> {
  const spent = new Map<string, number>(active.map((r) => [r.id, 0]));
  const started = active.flatMap((r) => (r.startedAt === null ? [] : [{ id: r.id, from: utcDay(r.startedAt) }]));
  if (started.length === 0) return spent;
  const first = Math.min(...started.map((r) => r.from));
  for (let t = first, end = utcDay(today); t < end; t += DAY_MS) {
    if (!isWeekday(t)) continue;
    const on = started.filter((r) => r.from <= t);
    for (const r of on) spent.set(r.id, (spent.get(r.id) ?? 0) + perWeekday / on.length);
  }
  // Ten additions of 0.8 give 7.999…; a hair is not a missed day.
  for (const [id, v] of spent) spent.set(id, Math.round(v * 1e6) / 1e6);
  return spent;
}

function toQueued(r: RequestSummary, elapsed: number): QueuedRequest {
  const estimate = r.estimateDays;
  let remaining: number | null = estimate;
  let overBy = 0;
  if (estimate !== null && elapsed > 0) {
    remaining = Math.max(0, estimate - elapsed);
    overBy = Math.max(0, elapsed - estimate);
  }
  return { request: r, estimateDays: estimate, remainingDays: remaining, overBy, cumulativeDays: null, projected: null, risk: { kind: "none" } };
}

const sum = (items: { estimateDays: number | null }[]): number => items.reduce((n, i) => n + (i.estimateDays ?? 0), 0);

/**
 * Build the whole view.
 *
 * `rows` must be in the server's canonical order (`fetchRequests` returns it
 * that way): the per-person queue is that order filtered, never re-sorted
 * here, so there is one definition of "next" in the system. `members` is the
 * roster; anyone who owns a row but is missing from it still gets a card —
 * the roster is empty for the frames before /api/team resolves, and for good
 * if that fetch fails, and a view that accounts for all the work must not
 * quietly show less of it.
 */
export function buildCapacity(
  rows: RequestSummary[],
  members: (Owner & { capacityDaysPerWeek: number | null })[],
  { now = new Date(), weeks = 2 }: { now?: Date; weeks?: number } = {},
): CapacityView {
  const today = todayIso(now);
  const open = rows.filter((r) => classOf(r) !== "closed");

  const byId = new Map(members.map((m) => [m.id, m]));
  for (const r of open) if (r.owner && !byId.has(r.owner.id)) byId.set(r.owner.id, { ...r.owner, capacityDaysPerWeek: null });

  const people = [...byId.values()].map((m): PersonCapacity => {
    const mine = open.filter((r) => r.owner?.id === m.id);
    const capacity = capacityFor(m);
    const perWeekday = capacity / 5;
    const activeRows = mine.filter((r) => classOf(r) === "active");
    const spent = elapsedByItem(activeRows, today, perWeekday);
    const active = activeRows.map((r) => toQueued(r, spent.get(r.id) ?? 0));
    const queue = mine.filter((r) => classOf(r) === "queued").map((r) => toQueued(r, 0));
    const parkedRows = mine.filter((r) => classOf(r) === "parked");
    const verifyingRows = mine.filter((r) => classOf(r) === "verifying");

    const ordered = [...active, ...queue];
    project(ordered, today, capacity);
    for (const item of ordered) item.risk = riskOf(item.request, item.projected);

    const committedItems = ordered.filter((i) => i.remainingDays !== null);
    return {
      owner: m,
      capacityDaysPerWeek: capacity,
      availableDays: capacity * weeks,
      committed: { days: committedItems.reduce((n, i) => n + (i.remainingDays ?? 0), 0), unestimated: ordered.length - committedItems.length },
      parked: { count: parkedRows.length, days: sum(parkedRows) },
      verifying: { count: verifyingRows.length, days: sum(verifyingRows) },
      active,
      queue,
    };
  });

  people.sort((a, b) => b.committed.days - a.committed.days || b.committed.unestimated - a.committed.unestimated || a.owner.name.localeCompare(b.owner.name));

  const nobodys = open.filter((r) => r.owner === null);
  return {
    today,
    weeks,
    people,
    unassigned: { count: nobodys.length, days: sum(nobodys), unestimated: nobodys.filter((r) => r.estimateDays === null).length },
  };
}
